import Cookies from "js-cookie";
import type { UserProfile, WebShellIdentity } from "@nike/web-shell-types";
import type {
  User,
  Profile,
  UserManager as UserManagerType,
  WebStorageStateStore as WebStorageStateStoreType
} from "@nike/oidc-client";
import { HOST_NAME } from "@nike/web-shell-shared";
import { removeVisitData, writeOidcRedirectUrl } from "./storage";
import { webShellClientInfoDispatch } from "../internal/webShellClientInfo";
import { addPageAction } from "../newRelicUtils";
import { GuestSession } from "./guestSession";

type UserManager = { type: null; instance: null } | { type: `oidc`; instance: UserManagerType };

const isSwooshUser = (
  profile: Partial<{ userType?: string; swoosh?: boolean }> | undefined
): boolean => {
  if (profile?.userType === `EMPLOYEE` || profile?.swoosh) {
    return true;
  }
  return false;
};

// input data is either user.profile from oid userManager's .getUser or profile object from success callback of session.getUserProfile
const shapeUserProfile = (data: any): UserProfile => {
  const profile: UserProfile = {
    avatarUrl:
      data?.picture || data?.avatar_url || data?.entity?.avatar?.fullUrl || data?.avatarUrl,
    firstName: data?.given_name || data?.entity?.firstName || data?.firstName,
    altFirstName:
      data?.name?.kana?.given ||
      data?.name?.alternate?.given ||
      data?.entity?.jpFirstNameKana ||
      data?.altFirstName,
    altLastName:
      data?.name?.kana?.family ||
      data?.name?.alternate?.family ||
      data?.entity?.jpLastNameKana ||
      data?.altLastName,
    lastName: data?.family_name || data?.entity?.lastName || data?.lastName,
    email: data?.email || data?.entity?.account?.email,
    mobileNumber: data?.phone_number || data?.entity?.mobileNumber || data?.mobileNumber,
    upmId: data?.sub || data?.entity?.account?.id || data?.upmId,
    registeredCountry: data?.country,
    signInFlow: data?.flow,
    userType: `GUEST`
  };
  if (isSwooshUser(data)) {
    profile.userType = `SWOOSH`;
  } else if (profile.upmId) {
    profile.userType = `MEMBER`;
  }
  return profile;
};

///////////////////////////////
// OidcClient config
///////////////////////////////
// Oidc.Log.logger = console;
// Oidc.Log.level = Oidc.Log.INFO;

const callBackRouteRegex =
  /^(?:\/auth\/(?:login|logout|silent-renew))|^(?:\/(?:[a-z]{2}\/(?:[a-z]{2}(?:-[A-z]{4})?\/)?)?(?:login|register))/;
const isNotACallbackRoute = (): boolean =>
  callBackRouteRegex.exec(window.location.pathname) === null;

/**
 * @description OIDC session monitoring stands up a hidden `check_session_iframe` on the
 * authority (accounts.nike.com) to notice a sign-out that happened in another Nike tab. That
 * iframe has to read the authority's session cookie, which only works where the authority is
 * SAME-SITE with the site host -- true for nike.com / nike.com.cn, false for off-dotcom build
 * variants (Jordan Drops on 232323232323.com). In a cross-site frame Safari's ITP blocks the
 * cookie outright and Chrome/Firefox partition it, so the OP derives "no session", replies
 * `changed`, and SessionMonitor calls `querySessionStatus()` -- a `prompt=none` request that
 * comes back `login_required` and is rethrown (we don't set `monitorAnonymousSession`), raising
 * a false `userSignedOut`. The capability is therefore not merely degraded off-dotcom, it is
 * all false positives: it cannot ever observe a real cross-tab sign-out there. Variants in that
 * position opt out via NEXT_PUBLIC_DISABLE_SESSION_MONITOR and rely on the "ni_s" cookie plus
 * the focus/visibilitychange forwarder below, which is same-origin and unaffected. SWAG-10430.
 *
 * Must be exactly `"true"` to opt out. The deploy input is a boolean, so a variant that leaves it
 * off renders the string `"false"`, and variants built without it at all leave the var undefined —
 * neither may read as opt-in.
 */
const isSessionMonitorDisabled = (): boolean =>
  process.env.NEXT_PUBLIC_DISABLE_SESSION_MONITOR === `true`;

/**
 * @description Silent renew has the same cross-site problem as session monitoring above, and on
 * the same variants -- but it is not merely useless there, it is actively harmful. We request no
 * `offline_access`, so there is no refresh token and `signinSilent()` ALWAYS takes the
 * `prompt=none` iframe on accounts.nike.com. Cross-site, the authority withholds its cookie and
 * that iframe answers `login_required` after burning the client's full 10s frame timeout. Since
 * `_init` awaits the renewal and every accessor awaits `_init`, one dead renewal stalls
 * getAccessToken/getUserProfile/getIsLoggedIn for ~10s for every consumer on the page, and the
 * failing callback writes a `localStorage` OIDC error that surfaces as "session expired". Worse,
 * "ni_s" lives a year and is dropped only on explicit sign-out, so the expired-user arm of
 * `shouldSilentlySignIn` is true on EVERY load and the stall repeats indefinitely.
 *
 * Variants in that position opt out via NEXT_PUBLIC_DISABLE_SILENT_RENEW and recover the session
 * through an ordinary top-level `signinRedirect` instead, which works cross-site because a
 * top-level navigation sends the authority's `SameSite=Lax` cookie -- so the member is returned
 * to where they were without re-entering credentials. SWAG-10477. The permanent fix is to request
 * `offline_access` for those variants and renew via a first-party refresh-token exchange.
 *
 * Must be exactly `"true"` to opt out, for the same reason as the session-monitor flag above.
 */
const isSilentRenewDisabled = (): boolean =>
  process.env.NEXT_PUBLIC_DISABLE_SILENT_RENEW === `true`;

function createOIDCSettings(userStore: WebStorageStateStoreType): Object {
  return {
    authority: process.env.NEXT_PUBLIC_AUTHORITY,
    client_id: process.env.NEXT_PUBLIC_CLIENT_ID,
    redirect_uri: process.env.NEXT_PUBLIC_REDIRECT_URI ?? `${window.location.origin}/auth/login`,
    post_logout_redirect_uri:
      process.env.NEXT_PUBLIC_POST_LOGOUT_REDIRECT_URI ?? `${window.location.origin}/auth/logout`,
    silent_redirect_uri:
      process.env.NEXT_PUBLIC_SILENT_REDIRECT_URI ?? `${window.location.origin}/auth/silent-renew`,
    response_type: `code`,
    scope: `openid nike.digital profile email phone flow country`,
    loadUserInfo: false,
    automaticSilentRenew: !isSilentRenewDisabled(),
    userStore,
    monitorSession: isNotACallbackRoute() && !isSessionMonitorDisabled()
  };
}

export const IS_SIGNED_IN_COOKIE_NAME = `ni_s`;

const setIsSignedInCookie = (domain: string): void => {
  Cookies.set(IS_SIGNED_IN_COOKIE_NAME, `1`, { domain, path: `/`, expires: 365 });
};

const removeIsSignedInCookie = (domain: string): void => {
  Cookies.remove(IS_SIGNED_IN_COOKIE_NAME, { domain, path: `/` });
};

export const hasIsSignedInCookie = (): boolean => Cookies.get(IS_SIGNED_IN_COOKIE_NAME) === `1`;

const DEFAULT_TRUSTED_REFERRER_HOST = `nike`;

/**
 * Additional comma-separated substrings checked against `document.referrer` (on top of the
 * `nike` default) to decide whether a navigation originated from a trusted
 * (same-identity-authority) domain, for silent-signin purposes. Build variants for off-dotcom
 * domains (e.g. Jordan Drops on 232323232323.com) can add their own host substring via
 * NEXT_PUBLIC_TRUSTED_REFERRER_HOSTS -- this is genuinely additive: the `nike` default is always
 * included, mirroring how NEXT_PUBLIC_EXTRA_GS_ORIGINS is layered on top of NIKE_ORIGINS in
 * guestSession.ts, rather than replacing it.
 */
const getTrustedReferrerHosts = (): string[] =>
  [
    DEFAULT_TRUSTED_REFERRER_HOST,
    ...(process.env.NEXT_PUBLIC_TRUSTED_REFERRER_HOSTS ?? ``).split(`,`)
  ]
    .map((host) => host.trim())
    .filter(Boolean);

const isCrossSubdomainNavigation = (): boolean =>
  getTrustedReferrerHosts().some((host) => document.referrer.includes(host)) &&
  !document.referrer.includes(document.location.origin);

const shouldSilentlySignIn = (user: User | null | undefined): boolean =>
  Boolean(
    ((isCrossSubdomainNavigation() && hasIsSignedInCookie()) || user?.expired) &&
      isNotACallbackRoute()
  );

/**
 * @description The counterpart to {@link isSilentRenewDisabled}: with no renewal path an expired
 * user is unrecoverable state rather than a session. Left in the store it makes getUserProfile()
 * report a member and getAccessToken() hand out a dead token, so the consumer renders a signed-in
 * shopper whose every request 401s. Discarding it presents the truth -- anonymous -- which routes
 * them through the consumer's own sign-in CTA and back to a real session.
 *
 * Skipped on the callback routes for the same reason `shouldSilentlySignIn` skips them:
 * /auth/login is where the recovering `signinRedirect` lands, and the expired user it would clear
 * is the one `signinCallback` is about to replace.
 *
 * Callers decide the user is expired; this deliberately does not re-read the store to confirm it.
 * `getUser()` runs `AccessTokenEvents.load`, which re-arms the expiry timer, and `Timer.init`
 * clamps the non-positive duration of an already-expired token to one second -- so confirming the
 * expiry from inside the expiry handler would rebuild the timer that just fired.
 *
 * Reports instead of throwing: the handler's promise is dropped by the synchronous `Event.raise`,
 * and `getUser` must not start throwing at consumers that only ever saw it resolve.
 *
 * @returns whether the user must stop being served -- true even when the removal itself failed,
 * because an expired user is never safe to hand out.
 */
const discardExpiredUser = async (userManager: UserManager): Promise<boolean> => {
  if (!isNotACallbackRoute()) return false;
  try {
    await userManager.instance?.removeUser();
  } catch {
    addPageAction(`WEB_SHELL_CLIENT_IDENTITY_DISCARD_EXPIRED_USER_ERROR`);
  }
  return true;
};

/** @description Clears OIDC Client identity data from the browser */
const clearOidcDataForSubdomains = async (userManager: UserManager): Promise<void> => {
  await userManager.instance.removeUser();
};

export function makeIdentity(): WebShellIdentity {
  const gs = new GuestSession();
  // Build variants for domains other than nike.com / nike.com.cn (e.g. Jordan Drops on
  // 232323232323.com) must set NEXT_PUBLIC_COOKIE_DOMAIN explicitly, since the "ni_s" cookie
  // has to be scoped to their own apex domain rather than falling through to nike.com.cn.
  const domain =
    process.env.NEXT_PUBLIC_COOKIE_DOMAIN ??
    (HOST_NAME === `www.nike.com` ? `nike.com` : `nike.com.cn`);

  const _init = async (): Promise<UserManager> => {
    let userManager: UserManager = { type: null, instance: null };
    const webShellClientVersion = process.env.WEB_SHELL_CLIENT_VERSION ?? ``;

    const isClient = typeof window !== `undefined`;

    try {
      if (isClient) {
        let signedInCookieIsPresent = hasIsSignedInCookie();

        window.newrelic?.addPageAction(`WEB_SHELL_CLIENT_IDENTITY_INITIALIZED`, {
          webShellClientVersion
        });

        // dynamically import oidc client
        const { UserManager, WebStorageStateStore } = await import(`@nike/oidc-client`);

        const userStore = new WebStorageStateStore({ store: window.localStorage });
        const OIDCSettings = createOIDCSettings(userStore);
        userManager = { type: `oidc`, instance: new UserManager(OIDCSettings) };
        // If the user is not signed in, we clear the OIDC Client data from the browser to ensure subdomains are signed out:
        if (!signedInCookieIsPresent) await clearOidcDataForSubdomains(userManager);

        // removed accounts visitData implementation (PR: https://github.com/nike-internal/dotcom.web-shell/pull/576)
        // use silent-renew feature if appropriate
        const user = await userManager.instance.getUser();
        if (isSilentRenewDisabled()) {
          // A user that is valid right now expires while the page stays open, and with
          // automaticSilentRenew off nothing in the client reacts to that. The expiry timer is
          // armed by the getUser() above regardless of the renew setting, and it re-reads the
          // wall clock every 5s rather than trusting one long timeout, so it still fires on a
          // device that slept through the expiration. The event firing is itself proof of the
          // expiry, which is why the handler passes no user -- see discardExpiredUser.
          userManager.instance.events.addAccessTokenExpired(() => discardExpiredUser(userManager));
          if (user?.expired) await discardExpiredUser(userManager);
        } else if (shouldSilentlySignIn(user)) {
          await userManager.instance.signinSilent();
        }

        // When a user returns to a Nike webpage: check if the "ni_s" (sign in) cookie has changed
        // If so, send the "nikeSignOut" event to teams consuming Web Shell Client
        const createSignOutEventForwarder = (): void => {
          const dispatchSignOutOnCookieRemoved = (): void => {
            // We check 'signedInCookie' is present to ensure the signOut event only fires on sign out
            // Otherwise, this event would fire each time the window is focused
            if (signedInCookieIsPresent && !hasIsSignedInCookie()) {
              signedInCookieIsPresent = false;
              window.dispatchEvent(new Event(`nikeSignOut`));
            }
          };
          // The focus event checks: when a window is focused
          window.addEventListener(`focus`, dispatchSignOutOnCookieRemoved);
          // The visibility change event checks: when a tab is selected or window is un-minimized
          window.addEventListener(`visibilitychange`, () => {
            if (document.visibilityState === `visible`) dispatchSignOutOnCookieRemoved();
          });
        };
        createSignOutEventForwarder();

        // putting intializeVisitData here b/c the tests were erroring out for getInitialized
        gs.initializeVisitData();
      }

      webShellClientInfoDispatch.initializeIdentity(`accounts`);
    } catch {
      window.newrelic?.addPageAction(`WEB_SHELL_CLIENT_IDENTITY_INITIALIZE_ERROR`, {
        webShellClientVersion
      });
    }

    return userManager;
  };

  const userManagerPromise = _init();

  /**
   * This function is only used by the OIDC callback route
   * and should NOT be used directly
   */
  const processSignIn: WebShellIdentity[`processSignIn`] = async () => {
    try {
      const userManager = await userManagerPromise;
      const user = await userManager.instance?.signinCallback();
      setIsSignedInCookie(domain);
      return user;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_PROCESS_SIGN_IN_ERROR`);
      throw e;
    }
  };

  const processSignOut: WebShellIdentity[`processSignOut`] = async () => {
    try {
      const userManager = await userManagerPromise;
      const response = await userManager.instance?.signoutCallback();
      removeVisitData();
      window.dispatchEvent(new Event(`nikeSignOut`));
      return response;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_PROCESS_SIGN_OUT_ERROR`);
      throw e;
    }
  };

  const getUser: WebShellIdentity[`getUser`] = async () => {
    const userManager = await userManagerPromise;
    const user = await userManager.instance?.getUser();
    // Every accessor reads the store through here, so this is the one place that can guarantee
    // none of them ever answers with a dead token. The timer above is what normally discards the
    // user; this covers the window before it fires -- a background tab throttled to minutes, or a
    // user written after initialization -- rather than duplicating it.
    if (isSilentRenewDisabled() && user?.expired && (await discardExpiredUser(userManager)))
      return undefined;
    return user;
  };

  const getUserProfile: WebShellIdentity[`getUserProfile`] = async () => {
    try {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_USER_PROFILE`);
      const user = await getUser();
      if (user) {
        return shapeUserProfile(user.profile);
      }
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_USER_PROFILE_ERROR`);
      throw e;
    }
  };

  const getIsMobileVerified: WebShellIdentity[`getIsMobileVerified`] = async () => {
    try {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_IS_MOBILE_VERIFIED`);
      const user = await getUser();

      return (user?.profile as Profile | undefined)?.phone_number_verified === true;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_IS_MOBILE_VERIFIED_ERROR`);
      throw e;
    }
  };

  const getIsSwooshUser: WebShellIdentity[`getIsSwooshUser`] = async () => {
    try {
      const user: { profile?: Object } | null | undefined = await getUser();
      const isSwoosh = isSwooshUser(user?.profile);
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_IS_SWOOSH_USER`, {
        isSwoosh
      });
      return isSwoosh;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_IS_SWOOSH_USER_ERROR`);
      throw e;
    }
  };

  // Modified to take into account of guest session iframe
  const getVisitData: WebShellIdentity[`getVisitData`] = async () => {
    try {
      if (window.nike?.unite?.isInitialized) {
        // For China guest sessions
        return window.nike.unite.session.getVisitData();
      }
      return await gs.getVisitData();
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_VISIT_DATA_ERROR`);
      throw e;
    }
  };

  const signIn: WebShellIdentity[`signIn`] = async (args = {}) => {
    const userManager = await userManagerPromise;
    try {
      const currentUrl = window.location.href;
      const { hreflang } = window.webShellClient.locale.get();
      const updatedArgs = {
        ...args,
        ui_locales: hreflang
      };
      const state = {
        ...updatedArgs,
        redirectUrl: currentUrl
      };
      // Store redirect URL in localStorage to track which URL initiated this OIDC flow
      writeOidcRedirectUrl(currentUrl);
      return userManager.instance?.signinRedirect({
        ...updatedArgs,
        state
      });
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_SIGN_IN_ERROR`);
      throw e;
    }
  };

  const signOut: WebShellIdentity[`signOut`] = async (args = {}) => {
    try {
      if (window.nike?.unite?.isInitialized) {
        // For China guest sessions
        window.nike.unite.session.logout();
      } else {
        gs.resetVisitData();
      }
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_SIGN_OUT`);
      const { hreflang } = window.webShellClient.locale.get();
      const currentUrl = window.location.href;
      const updatedArgs = {
        ...args,
        ui_locales: hreflang
      };
      const state = {
        ...updatedArgs,
        redirectUrl: currentUrl
      };

      removeIsSignedInCookie(domain);
      // Store redirect URL in localStorage to track which URL initiated this OIDC flow
      writeOidcRedirectUrl(currentUrl);

      const userManager = await userManagerPromise;
      window.dispatchEvent(new Event(`nikeSignOut`));
      return userManager.instance?.signoutRedirect({
        ...updatedArgs,
        state
      });
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_SIGN_OUT_ERROR`);
      throw e;
    }
  };

  const getIsLoggedIn: WebShellIdentity[`getIsLoggedIn`] = async () => {
    const profile = await getUserProfile();

    if (profile) {
      return profile.userType !== `GUEST`;
    }

    return !!profile;
  };

  /**
   * @returns {Promise<string|undefined>} access token if it exists
   */
  const getAccessToken: WebShellIdentity[`getAccessToken`] = async () => {
    try {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_ACCESS_TOKEN`);

      // Must ensure that silent-renew is finished before getting token.
      const user = await getUser();

      if (user) {
        return user.access_token;
      }
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_ACCESS_TOKEN_ERROR`);
      throw e;
    }
  };

  const getInitialized: WebShellIdentity[`getInitialized`] = async () => {
    try {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_INITIALIZED`);
      const userManager = await userManagerPromise;
      return !!userManager;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_GET_INITIALIZED_ERROR`);
      throw e;
    }
  };

  /**
   * This function is used ONLY internally by the OIDC callback route
   * (i.e. www.nike.com/auth/silent-renew) and should NOT be used
   * directly
   * @returns
   */
  const processReauth: WebShellIdentity[`processReauth`] = async () => {
    try {
      const userManager = await userManagerPromise;

      if (userManager.type !== `oidc`) {
        return;
      }

      const user = await userManager.instance.signinSilentCallback();
      if (user) {
        setIsSignedInCookie(domain);
      }
      return user;
    } catch (e: unknown) {
      addPageAction(`WEB_SHELL_CLIENT_IDENTITY_PROCESS_REAUTH_ERROR`);
      throw e;
    }
  };

  const identityObject: WebShellIdentity = {
    /**
     * @deprecated identity module is now self-initializing,
     * this method call can be safely removed
     */
    initialize: async () => {
      await userManagerPromise;
      return identityObject;
    },
    getAccessToken,
    getInitialized,
    getIsLoggedIn,
    getIsMobileVerified,
    getIsSwooshUser,
    getUser,
    getUserProfile,
    getVisitData,
    processReauth,
    processSignIn,
    processSignOut,
    signIn,
    signOut
  };
  return identityObject;
}
